import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import InputOtp from 'components/UI/InputOtp';
import Modal from 'components/Modal';
import Translate from 'components/Translate';
import { OTP_LENGTH } from 'constants/global';
import { GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import SuccessModal from 'components/FeedbackModals/SuccessModal';
import { openSupport } from 'utils/openSupport';
import StepHeader from './StepHeader';

const MAX_TIME = 300000;
const RESEND_TIME = 40000;

const ClientOTPValidation = ({
  literals,
  user,
  onContinue,
  onResend,
  onBack,
  onEvent,
  onError,
}) => {
  const [timer, setTimer] = useState({
    sms: { start: new Date().getTime(), left: MAX_TIME / 1000, resend: RESEND_TIME / 1000 },
    email: { start: new Date().getTime(), left: MAX_TIME / 1000, resend: RESEND_TIME / 1000 },
  });
  const [otp, setOtp] = useState({ email: '', phone: '' });
  const [confirmResend, setConfirmResend] = useState(null);
  const [successResend, setSuccessResend] = useState(false);
  const timerRef = useRef(null);

  const formatTime = (time) => {
    if (time <= 0) {
      return '00:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        const now = new Date().getTime();
        return {
          sms: {
            ...prev.sms,
            left: Math.floor((MAX_TIME - (now - prev.sms.start)) / 1000),
            resend: Math.floor((RESEND_TIME - (now - prev.sms.start)) / 1000),
          },
          email: {
            ...prev.email,
            left: Math.floor((MAX_TIME - (now - prev.email.start)) / 1000),
            resend: Math.floor((RESEND_TIME - (now - prev.email.start)) / 1000),
          },
        };
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  const handleResend = (type) => {
    if (confirmResend) {
      onEvent(
        GA_REGISTRATION_ACTIONS.EVENT,
        GA_REGISTRATION_LABELS.OTP_VALIDATION_RESEND,
      );
      onResend(type).then(() => {
        setSuccessResend(true);
        setTimer((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            start: new Date().getTime(),
            left: MAX_TIME / 1000,
            resend: RESEND_TIME / 1000,
          },
        }));
      }).catch(() => {
        onError(literals.OTPValidation.errorResend);
      }).finally(() => {
        setConfirmResend(null);
      });
    } else {
      setConfirmResend(type);
    }
  };

  const handleContinue = () => {
    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      GA_REGISTRATION_LABELS.OTP_VALIDATION_SUBMIT,
    );
    onContinue(otp);
  };

  const canContinue = otp.phone.length < OTP_LENGTH || otp.email.length < OTP_LENGTH;

  return (
    <div className='full-screen-container'>
      <div className='screen-content'>
        <StepHeader step={3} onEvent={onEvent} />
        <p className='font-bold text-primary mb-4 text-xl'>{literals.OTPValidation.smsVerification}</p>
        <Translate className='mb-6' literal={literals.OTPValidation.smsVerificationDescription} vars={{ phone: user.phone }} />
        <p className='text-sm mb-6 underline' onClick={onBack}>{literals.OTPValidation.itsNotMyPhone}</p>
        <div className='flex justify-between'>
          <p className='font-black'>{literals.OTPValidation.codeExpirationTime}</p>
          <p className='font-bold text-primary'>{`${formatTime(timer.sms.left)} min`}</p>
        </div>
        <InputOtp
          className='mb-4'
          value={otp.phone}
          onChange={(phone) => setOtp({ ...otp, phone })}
        />
        <p
          className='text-sm mb-6 underline'
          onClick={() => handleResend('sms')}
        >
          {literals.OTPValidation.notReceivedCode}
        </p>
        <p className='font-bold text-primary mb-4 text-xl'>{literals.OTPValidation.emailVerification}</p>
        <Translate className='mb-6' literal={literals.OTPValidation.emailVerificationDescription} vars={{ email: user.email }} />
        <p className='text-sm mb-6 underline' onClick={onBack}>{literals.OTPValidation.itsNotMyEmail}</p>
        <div className='flex justify-between'>
          <p className='font-black'>{literals.OTPValidation.codeExpirationTime}</p>
          <p className='font-bold text-primary'>{`${formatTime(timer.email.left)} min`}</p>
        </div>
        <InputOtp
          className='mb-4'
          value={otp.email}
          onChange={(email) => setOtp({ ...otp, email })}
        />
        <p
          className='text-sm mb-6 underline'
          onClick={() => handleResend('email')}
        >
          {literals.OTPValidation.notReceivedCode}
        </p>
        {confirmResend && (
          <Modal title={literals.OTPValidation.resendCode} position='end' onClose={() => setConfirmResend(null)} animation='slide-up-enter'>
            <Translate
              className='mt-2'
              literal={timer[confirmResend].resend > 0 ? literals.OTPValidation.waitToSendCode : literals.OTPValidation.confirmResend}
              vars={{ time: timer[confirmResend].resend }}
            />
            <Button
              className='mt-2'
              text={literals.OTPValidation.resend}
              disabled={timer[confirmResend].resend > 0}
              onClick={() => handleResend(confirmResend)}
            />
            <Button
              className='mt-2'
              type='outline-primary'
              text={literals.common.cancel}
              onClick={() => setConfirmResend(null)}
            />
          </Modal>
        )}
        {successResend && (
          <SuccessModal
            title={literals.OTPValidation.resendSuccess}
            description={literals.OTPValidation.resendCodeSuccess}
            onClose={() => setSuccessResend(false)}
          >
            <Button
              className='mt-6'
              text={literals.common.close}
              onClick={() => setSuccessResend(false)}
            />
          </SuccessModal>
        )}
      </div>
      <div className='screen-actions'>
        <Button onClick={handleContinue} disabled={canContinue}>{literals.common.next}</Button>
        <Button type='outline-primary' onClick={openSupport}>{literals.contactSupport}</Button>
      </div>
    </div>
  );
};

ClientOTPValidation.propTypes = {
  literals: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onContinue: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onEvent: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default ClientOTPValidation;
